// AdminKpisPontualidadePage — % de chegadas pontuais por técnico
// Pontual = início do atendimento dentro de +10min da janela_inicio.
// Considera só OS com início registrado nos últimos N dias.

import { useEffect, useState, useMemo } from 'react';
import { admin } from '../../lib/api.js';

const PERIODOS = [7, 30, 90];

function formatMin(m) {
  if (m == null) return '—';
  const n = Number(m);
  if (Math.abs(n) < 1) return '0min';
  const sinal = n > 0 ? '+' : '';
  return `${sinal}${n.toFixed(1)}min`;
}

function CorPct({ pct }) {
  if (pct == null) return <span style={{ color: '#9ca3af' }}>—</span>;
  const cor = pct >= 70 ? '#059669' : pct >= 40 ? '#d97706' : '#dc2626';
  return <span style={{ color: cor, fontWeight: 700 }}>{pct}%</span>;
}

function BarraPct({ pct }) {
  const v = Math.max(0, Math.min(100, pct || 0));
  const cor = v >= 70 ? '#059669' : v >= 40 ? '#d97706' : '#dc2626';
  return (
    <div style={{
      height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden',
      width: 120,
    }}>
      <div style={{ height: '100%', width: `${v}%`, background: cor, transition: 'width 0.3s' }}/>
    </div>
  );
}

export default function AdminKpisPontualidadePage() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    admin.kpisPontualidade(dias)
      .then(r => { if (alive) { setData(r); setErro(null); } })
      .catch(e => { if (alive) setErro(e.uiMessage || 'Erro'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dias]);

  const g = data?.globais || {};
  const ranking = data?.ranking || [];
  const ts = data?.meta?.ts;

  const top3 = ranking.slice(0, 3);
  const bot3 = useMemo(() => [...ranking].slice(-3).reverse(), [ranking]);

  return (
    <div className="stack">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ margin: 0 }}>⏰ Pontualidade dos técnicos</h1>
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIODOS.map(d => (
            <button key={d} type="button"
              className={`btn small ${dias === d ? 'primary' : 'secondary'}`}
              onClick={() => setDias(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      <p className="muted" style={{ marginTop: -8, fontSize: 13 }}>
        Pontual = início do atendimento dentro de ±10min da janela contratada.
        Considera só OS com início registrado. {data?.meta && (
          <span style={{ color: '#9ca3af', marginLeft: 6 }}>
            Atualizado: {new Date(ts).toLocaleTimeString('pt-BR')}
          </span>
        )}
      </p>

      {erro && <div className="card error">{erro}</div>}
      {loading && <div className="muted">Carregando…</div>}

      {data && !loading && (
        <>
          {/* KPIs globais */}
          <div className="kpi-cards" style={{ marginBottom: 16 }}>
            <div className="kpi-card">
              <div className="kpi-label">Total de OS analisadas</div>
              <div className="kpi-value">{g.total}</div>
              <div className="kpi-sublabel">{g.dados_descartados > 0 ? `+${g.dados_descartados} descartadas (dados sujos)` : 'todas válidas'}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">% Pontuais</div>
              <div className="kpi-value" style={{ color: g.pct_pontuais >= 70 ? '#059669' : g.pct_pontuais >= 40 ? '#d97706' : '#dc2626' }}>
                {g.pct_pontuais}%
              </div>
              <div className="kpi-sublabel">{g.pontuais} de {g.total} OS</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Atraso médio</div>
              <div className="kpi-value">{formatMin(g.atraso_medio_min)}</div>
              <div className="kpi-sublabel">mediano: {formatMin(g.atraso_mediano_min)}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Atrasos graves (&gt;30min)</div>
              <div className="kpi-value" style={{ color: '#dc2626' }}>{g.atrasadas_grave}</div>
              <div className="kpi-sublabel">{g.total > 0 ? Math.round(g.atrasadas_grave * 100 / g.total) : 0}% do total</div>
            </div>
          </div>

          {/* Distribuição visual */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>Distribuição das OS</h3>
            {(() => {
              const partes = [
                { label: 'Adiantadas (<0min)',   n: g.adiantadas,       cor: '#3b82f6' },
                { label: 'Pontuais (≤10min)',     n: g.pontuais - g.adiantadas, cor: '#059669' },
                { label: 'Leves (10-30min)',      n: g.atrasadas_leve,   cor: '#d97706' },
                { label: 'Graves (>30min)',       n: g.atrasadas_grave,  cor: '#dc2626' },
              ];
              const total = g.total || 1;
              return (
                <>
                  <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
                    {partes.map(p => p.n > 0 && (
                      <div key={p.label} style={{
                        width: `${(p.n / total) * 100}%`,
                        background: p.cor, color: 'white',
                        fontSize: 11, fontWeight: 600,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }} title={`${p.label}: ${p.n}`}>
                        {p.n > total * 0.07 ? p.n : ''}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 11 }}>
                    {partes.map(p => (
                      <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 10, height: 10, background: p.cor, borderRadius: 2 }}></span>
                        {p.label}: <strong>{p.n}</strong>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Top 3 + Piores 3 */}
          {ranking.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="card" style={{ background: '#f0fdf4' }}>
                <h3 style={{ marginTop: 0, fontSize: 14, color: '#059669' }}>🏆 Top 3 mais pontuais</h3>
                {top3.map((r, i) => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 2 ? '1px solid #d1fae5' : 'none' }}>
                    <div>
                      <strong>{['🥇','🥈','🥉'][i]} {r.apelido}</strong>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{r.total} OS · médio {formatMin(r.atraso_medio_min)}</div>
                    </div>
                    <CorPct pct={r.pct_pontuais} />
                  </div>
                ))}
              </div>
              <div className="card" style={{ background: '#fef2f2' }}>
                <h3 style={{ marginTop: 0, fontSize: 14, color: '#dc2626' }}>⚠️ Piores 3</h3>
                {bot3.map((r, i) => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 2 ? '1px solid #fecaca' : 'none' }}>
                    <div>
                      <strong>{r.apelido}</strong>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{r.total} OS · médio {formatMin(r.atraso_medio_min)}</div>
                    </div>
                    <CorPct pct={r.pct_pontuais} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabela completa */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Técnico</th>
                  <th style={{ textAlign: 'right' }}>OS</th>
                  <th>% Pontuais</th>
                  <th style={{ textAlign: 'right' }}>Atraso médio</th>
                  <th style={{ textAlign: 'right' }}>Atraso mediano</th>
                  <th style={{ textAlign: 'right' }}>Atrasos &gt;30min</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.apelido}</strong><div style={{ fontSize: 10, color: '#9ca3af' }}>{r.nome}</div></td>
                    <td style={{ textAlign: 'right' }}>{r.total}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <BarraPct pct={r.pct_pontuais} />
                        <CorPct pct={r.pct_pontuais} />
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatMin(r.atraso_medio_min)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatMin(r.atraso_mediano_min)}</td>
                    <td style={{ textAlign: 'right', color: r.atrasadas_grave > 10 ? '#dc2626' : '#6b7280' }}>
                      {r.atrasadas_grave} ({r.total > 0 ? Math.round(r.atrasadas_grave * 100 / r.total) : 0}%)
                    </td>
                  </tr>
                ))}
                {ranking.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
                    Nenhum técnico com OS suficiente (mínimo 5 OS no período).
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="alert-block" style={{ background: '#eff6ff', borderLeftColor: '#3b82f6', color: '#1e40af', fontSize: 12 }}>
            💡 <strong>Como ler:</strong> "% Pontuais" considera chegada até 10min após a janela contratada
            (adiantadas também contam). Atraso médio negativo significa que em média o técnico chega antes do horário.
            Apenas técnicos com 5+ OS no período aparecem no ranking pra evitar viés de amostra.
          </div>
        </>
      )}
    </div>
  );
}
